import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { useFormat } from '../../../../hooks/index';
import { antiDilutionBaseTitle } from '../../../../types/share-classes.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { RecordAttachedFilesPreview } from '../../../pool-plans/components/record-attached-files-preview';
import { ChildrenFormProps } from '../modals.types';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  formData,
  isLoading,
}) => {
  const { stepOne, stepFour, stepThree, stepTwo } = formData();
  const { format } = useFormat();

  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="SHARE_CLASS_NAME" />}
              value={stepOne.name}
            />

            <SummaryItem
              title={<AppFormattedMessage id="CREATION_DATE" />}
              value={stepOne?.creationDate && format(stepOne.creationDate, 'dd/MM/yyyy')}
            />

            <div className="flex flex-col gap-3 py-3">
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="VOTING_RIGHT" />}
                value={
                  stepOne.votingRight?.enabled ? (
                    <AppFormattedMessage id="YES" />
                  ) : (
                    <AppFormattedMessage id="NO" />
                  )
                }
              />

              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                value={stepOne.votingRight?.value || '-'}
              />
            </div>
            <div className="flex flex-col gap-3 py-3">
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="CONVERSION_RATIO" />}
                value={
                  stepOne.conversionRatio?.enabled ? (
                    <AppFormattedMessage id="YES" />
                  ) : (
                    <AppFormattedMessage id="NO" />
                  )
                }
              />
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                value={stepOne.conversionRatio?.value || '-'}
              />
            </div>
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="DIVIDEND_RIGHT" />}
              value={
                stepOne.dividendRightEnabled ? (
                  <AppFormattedMessage id="YES" />
                ) : (
                  <AppFormattedMessage id="NO" />
                )
              }
            />
          </div>
        </div>
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="LIQUIDITY" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <div className="flex flex-col gap-3 pb-3">
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="SENIORITY" />}
                value={stepTwo?.liquidityPreferences?.seniority || '-'}
              />

              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                value={stepTwo?.liquidityPreferences?.multiple || '-'}
              />
            </div>
            <div className="flex flex-col gap-3 pt-3">
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="PARTICIPATING" />}
                value={
                  stepTwo?.liquidityPreferences?.participating?.enabled ? (
                    <AppFormattedMessage id="YES" />
                  ) : (
                    <AppFormattedMessage id="NO" />
                  )
                }
              />
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="CAP_VALUE" />}
                value={stepTwo?.liquidityPreferences?.participating?.capValue || '-'}
              />
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="INTEREST" />}
                value={
                  stepTwo?.liquidityPreferences?.participating?.interest
                    ? stepTwo?.liquidityPreferences?.participating?.interest + '%'
                    : '-'
                }
              />
              <SummaryItem
                className="py-0"
                title={<AppFormattedMessage id="DAYS_PER_YEAR" />}
                value={stepTwo?.liquidityPreferences?.participating?.daysPersYear || '-'}
              />
            </div>
          </div>
        </div>

        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="ANTI_DILUTION_RIGHTS" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="BASE" />}
              value={
                stepThree?.antiDilutionRights?.base ? (
                  <AppFormattedMessage
                    id={antiDilutionBaseTitle[stepThree?.antiDilutionRights?.base]}
                  />
                ) : (
                  '-'
                )
              }
            />

            {/* <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="PRE_EMPTIVE_RIGHTS" />}
              value={
                stepThree?.antiDilutionRights?.enabled ? (
                  <AppFormattedMessage id="YES" />
                ) : (
                  <AppFormattedMessage id="NO" />
                )
              }
            /> */}
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className={'flex gap-3 text-nowrap px-4 py-2'}>
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <RecordAttachedFilesPreview
              files={stepFour?.files}
              onDocumentClick={handleDocumentClick}
            />
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex w-full gap-3 px-4 py-2">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
              {stepFour?.additionalNotes || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="ADD_SHARE_CLASS" />
          </Button>
        </div>
      </div>
    </>
  );
};
